import { useEffect } from 'react';
import { createResizeObserver } from '../../../../utils/resizeObserver';

const DESKTOP_MQ = '(min-width: 961px)';

const getScrollSourceMetrics = (mode, feedEl) => {
  if (mode === 'internal' && feedEl) {
    return {
      scrollTop: feedEl.scrollTop,
      max: Math.max(0, feedEl.scrollHeight - feedEl.clientHeight),
      contentHeight: feedEl.scrollHeight,
    };
  }

  const docH = Math.max(document.documentElement?.scrollHeight || 0, document.body?.scrollHeight || 0);
  const max = Math.max(0, docH - window.innerHeight);
  return {
    scrollTop: window.scrollY || document.documentElement.scrollTop || 0,
    max,
    contentHeight: docH,
  };
};

const setScrollSourceTop = (mode, feedEl, top) => {
  const y = Math.max(0, Math.round(top));
  if (mode === 'internal' && feedEl) {
    feedEl.scrollTop = y;
    return;
  }
  window.scrollTo({ top: y, left: 0, behavior: 'auto' });
};

const getFeedScrollMode = (feedEl) => {
  if (!(feedEl instanceof HTMLElement)) return null;
  const { overflowY } = window.getComputedStyle(feedEl);
  if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return 'internal';
  return 'window';
};

const mapScrollTop = (sourceTop, sourceMax, targetMax) => {
  if (sourceMax <= 0 || targetMax <= 0) return 0;
  return (sourceTop / sourceMax) * targetMax;
};

const mapScrollTopInverse = (targetTop, targetMax, sourceMax) => {
  if (targetMax <= 0 || sourceMax <= 0) return 0;
  return (targetTop / targetMax) * sourceMax;
};

/**
 * Desktop-only page-edge scrollbar that proxies the posts feed scrollport.
 * Feed remains the scroll source; the port only mirrors unless the user drags it.
 */
export function usePostsFeedPageScrollbar(feedRef, { enabled = true } = {}) {
  useEffect(() => {
    if (!enabled) return undefined;

    const mq = window.matchMedia(DESKTOP_MQ);
    let port = null;
    let spacer = null;
    let mode = null;
    let feedEl = null;
    let headerEl = null;
    let roFeed = null;
    let roHeader = null;
    let roDocument = null;
    let ignorePortScroll = false;
    let ignoreFeedScroll = false;
    let rafId = 0;

    const syncHeaderOffset = () => {
      const top = headerEl ? Math.ceil(headerEl.getBoundingClientRect().bottom) : 0;
      document.documentElement.style.setProperty('--od-feed-page-scroll-top', `${top}px`);
      port?.style.setProperty('--od-feed-page-scroll-top', `${top}px`);
    };

    const portMetrics = () => {
      if (!port || !spacer) return { max: 0 };
      return { max: Math.max(0, spacer.offsetHeight - port.clientHeight) };
    };

    const updateSpacerHeight = () => {
      if (!spacer) return;
      const { contentHeight } = getScrollSourceMetrics(mode, feedEl);
      spacer.style.height = `${Math.max(contentHeight, 1)}px`;
    };

    const setPortScrollTop = (top) => {
      if (!port) return;
      ignorePortScroll = true;
      port.scrollTop = top;
      requestAnimationFrame(() => {
        ignorePortScroll = false;
      });
    };

    const syncPortFromSource = () => {
      if (!port || !spacer) return;
      const { scrollTop, max } = getScrollSourceMetrics(mode, feedEl);
      const { max: portMax } = portMetrics();
      const visible = max > 0 && portMax > 0;
      port.classList.toggle('is-visible', visible);
      document.documentElement.classList.toggle('od-feed-page-scrollbar-active', visible);
      if (!visible) return;
      setPortScrollTop(mapScrollTop(scrollTop, max, portMax));
    };

    const syncSourceFromPort = () => {
      if (!port || !spacer) return;
      const { max } = getScrollSourceMetrics(mode, feedEl);
      const { max: portMax } = portMetrics();
      if (max <= 0 || portMax <= 0) return;
      ignoreFeedScroll = true;
      setScrollSourceTop(mode, feedEl, mapScrollTopInverse(port.scrollTop, portMax, max));
      requestAnimationFrame(() => {
        ignoreFeedScroll = false;
      });
    };

    const schedulePortSync = (updateSpacer = true) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        if (updateSpacer) updateSpacerHeight();
        syncPortFromSource();
      });
    };

    const onFeedScroll = () => {
      if (ignoreFeedScroll) return;
      schedulePortSync(false);
    };

    const onWindowScroll = () => {
      if (mode !== 'window' || ignoreFeedScroll) return;
      schedulePortSync(false);
    };

    const onPortScroll = (event) => {
      if (ignorePortScroll || !event.isTrusted) return;
      syncSourceFromPort();
    };

    const onResize = () => {
      syncHeaderOffset();
      schedulePortSync(true);
    };

    const teardownListeners = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
      port?.removeEventListener('scroll', onPortScroll);
      feedEl?.removeEventListener('scroll', onFeedScroll);
      window.removeEventListener('scroll', onWindowScroll);
      window.removeEventListener('resize', onResize);
    };

    const cleanup = () => {
      teardownListeners();
      roFeed?.disconnect();
      roHeader?.disconnect();
      roDocument?.disconnect();
      roFeed = null;
      roHeader = null;
      roDocument = null;
      port?.remove();
      port = null;
      spacer = null;
      feedEl = null;
      headerEl = null;
      mode = null;
      ignorePortScroll = false;
      ignoreFeedScroll = false;
      document.documentElement.classList.remove('od-feed-page-scrollbar-active');
      document.documentElement.style.removeProperty('--od-feed-page-scroll-top');
    };

    const mount = () => {
      cleanup();
      if (!mq.matches) return;

      feedEl = feedRef.current;
      if (!(feedEl instanceof HTMLElement)) return;

      mode = getFeedScrollMode(feedEl);
      if (!mode) return;

      headerEl =
        feedEl.closest('.support-open-disc--home')?.querySelector('.support-open-disc-shell-header') || null;

      port = document.createElement('div');
      port.className = 'od-feed-page-scrollport';
      port.setAttribute('aria-hidden', 'true');
      port.tabIndex = -1;

      spacer = document.createElement('div');
      spacer.className = 'od-feed-page-scrollport-spacer';
      port.appendChild(spacer);
      document.body.appendChild(port);

      syncHeaderOffset();
      updateSpacerHeight();
      syncPortFromSource();

      port.addEventListener('scroll', onPortScroll, { passive: true });
      feedEl.addEventListener('scroll', onFeedScroll, { passive: true });
      window.addEventListener('scroll', onWindowScroll, { passive: true });
      window.addEventListener('resize', onResize, { passive: true });

      roFeed = createResizeObserver(() => schedulePortSync(true));
      roFeed?.observe(feedEl);

      if (headerEl) {
        roHeader = createResizeObserver(onResize);
        roHeader.observe(headerEl);
      }

      if (mode === 'window' && document.documentElement) {
        roDocument = createResizeObserver(() => schedulePortSync(true));
        roDocument.observe(document.documentElement);
      }
    };

    const onMqChange = () => mount();

    mount();
    mq.addEventListener('change', onMqChange);

    return () => {
      mq.removeEventListener('change', onMqChange);
      cleanup();
    };
  }, [feedRef, enabled]);
}
